import { type ReactNode, useState } from "react";
import { Layers, Loader2 } from "lucide-react";
import { useFlashcardsStore } from "@/stores/flashcardsStore";
import FlashcardsDeckListItem from "@/components/flashcards/FlashcardsDeckListItem";
import FlashcardsDeckStudyPane from "@/components/flashcards/FlashcardsDeckStudyPane";
import {
  ModalSplitLayout,
  ModalSplitTitleIcon,
} from "@/components/layout/ModalSplitLayout";
import { SearchField } from "@/components/ui/search-field";
import type { Deck } from "@/services/flashcardsService";

const filterDecksByTitle = (decks: Deck[], query: string): Deck[] => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return decks;
  }
  return decks.filter((deck) =>
    deck.deckTitle.toLowerCase().includes(normalizedQuery)
  );
};

const PickerStatus = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-12 text-center text-muted-foreground">
    {children}
  </div>
);

export const FlashcardsDeckPickerModal = () => {
  const { decks, decksLoading, decksError } = useFlashcardsStore();
  const [query, setQuery] = useState("");
  const [previewedDeckId, setPreviewedDeckId] = useState<string | null>(null);

  const filteredDecks = filterDecksByTitle(decks, query);
  const previewedDeck =
    filteredDecks.find((deck) => deck.id === previewedDeckId) ??
    filteredDecks[0] ??
    null;

  const renderDeckList = () => {
    if (filteredDecks.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center gap-2 px-2 py-8 text-center text-muted-foreground">
          <strong className="block font-bold text-primary">
            Keine Decks gefunden
          </strong>
          <span>Versuche es mit einem anderen Suchbegriff.</span>
        </div>
      );
    }

    return (
      <ul className="flex flex-col gap-2" aria-label="Decks">
        {filteredDecks.map((deck) => (
          <li key={deck.id}>
            <FlashcardsDeckListItem
              title={deck.deckTitle}
              cardCount={deck.totalCards}
              isSelected={deck.id === previewedDeck?.id}
              onSelect={() => setPreviewedDeckId(deck.id)}
            />
          </li>
        ))}
      </ul>
    );
  };

  const renderStudyColumn = () => {
    if (!previewedDeck) {
      return null;
    }

    return (
      <FlashcardsDeckStudyPane
        key={previewedDeck.id}
        deckTitle={previewedDeck.deckTitle}
        cards={previewedDeck.cards}
      />
    );
  };

  const renderBody = () => {
    if (decksLoading) {
      return (
        <PickerStatus>
          <Loader2
            className="size-8 animate-spin text-primary"
            aria-hidden="true"
          />
          <span role="status">Lernkarten werden geladen…</span>
        </PickerStatus>
      );
    }

    if (decksError) {
      return (
        <PickerStatus>
          <strong className="block font-bold text-primary">
            Konnte Lernkarten nicht laden
          </strong>
          <span>{decksError}</span>
        </PickerStatus>
      );
    }

    if (decks.length === 0) {
      return (
        <PickerStatus>
          <strong className="block font-bold text-primary">
            Noch keine Lernkarten verfügbar
          </strong>
          <span>
            Schließe Lernkarten in der Lektion ab, indem du jede Karte einmal
            umdrehst. Abgeschlossene Decks erscheinen hier zur Wiederholung.
          </span>
        </PickerStatus>
      );
    }

    return (
      <ModalSplitLayout
        asideClassName="lg:w-80"
        aside={
          <aside className="flex min-h-0 flex-1 flex-col gap-4 p-6">
            <div className="flex min-w-0 items-center gap-3 text-primary">
              <ModalSplitTitleIcon icon={Layers} />
              <div className="truncate text-heading-h2" aria-hidden="true">
                Lernkarten
              </div>
            </div>
            <SearchField
              placeholder="Lernkarten durchsuchen…"
              value={query}
              onChange={setQuery}
            />
            <div className="h-64 min-h-0 flex-1 overflow-y-auto lg:h-auto">
              {renderDeckList()}
            </div>
          </aside>
        }
        main={renderStudyColumn()}
      />
    );
  };

  return (
    <>
      <div id="lk-deck-picker-title" className="sr-only">
        Lernkarten
      </div>
      {renderBody()}
    </>
  );
};
